/* istanbul ignore file */
import AreaChartV2 from 'components/highcharts/AreaChartV2';
import {
  getMultiSeriesSharedPoints,
  getXAxisLabel,
  labelBuilder,
  onLegendSeriesClick
} from '../custom-report.serivce';
import { getToolTipTitleByDateRange, toolTipFormatter } from '../Tooltip';
import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import { useEffect, useState } from 'react';

const CustomReportAreaChart = ({
  searchRange,
  granularity,
  series,
  isClickable,
  getEndDateForTooltip,
  yFormatter,
  isMultiSeries,
  displayLabel,
  percentOffName,
  metricName,
  metricsCalculationConfig,
  onPointClick,
  onPointHover = _.noop,
  onAreaSelect,
  graphColor,
  toggleDisabledSeriesIdsByMetricName,
  workingDaysSetting,
  shouldHideClickableMessage
}) => {
  const [isMounted, setIsMounted] = useState(false);

  const seriesHandleClick = series.map(item => {
    const { isLineHidden, isPreventLineClick } = item;
    return {
      ...item,
      ...(!isMounted && { visible: !isLineHidden }),
      cursor: isClickable && !isPreventLineClick ? 'pointer' : 'default',
      events: {
        ...(!isPreventLineClick && {
          click: onPointClick
        })
      }
    };
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const chartType = _.get(series, '[0].type', 'areaspline');
  const isAreaSelectEnabled = !!onAreaSelect;
  const legendTotalStyle = 'font-weight: normal; padding-right: 0.3rem;';
  const legendNameStyle =
    'text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 8rem;';

  return (
    <AreaChartV2
      chartOptionsProp={{
        chart: {
          className: 'hideLeftXAxis',
          ...(isAreaSelectEnabled && {
            zoomType: 'x',
            selectionMarkerFill: getColor('pigeonPost', 0.25),

            style: {
              cursor: 'crosshair'
            }
          }),
          events: {
            ...(isAreaSelectEnabled && {
              selection: onAreaSelect
            })
          }
        },
        legend: {
          align: 'left',
          verticalAlign: 'top',
          useHTML: true,
          labelFormatter() {
            const { name, options } = this;
            const total = options?.total;

            if (!total) {
              return name;
            }

            if (series?.length <= 4) {
              return `<span style="${legendTotalStyle}">(${total})</span>${name}`;
            }

            return (
              `<div style="display: inline-flex; white-space: nowrap;">` +
              `<span style="${legendTotalStyle}">(${total})</span>` +
              `<div style="${legendNameStyle}">${name}</div>` +
              `</div>`
            );
          }
        },
        xAxis: {
          categories: searchRange,
          gridLineColor: 'transparent',
          ...(isAreaSelectEnabled && {
            crosshair: {
              enabled: true,
              snap: false,
              width: 2,
              color: getColor('pigeonPost'),
              dashStyle: 'shortdot'
            }
          }),
          labels: {
            formatter() {
              const displayEndDate = getEndDateForTooltip(this.value.before);
              return getXAxisLabel(
                this.value.after,
                displayEndDate,
                granularity,
                this.value.name,
                workingDaysSetting
              );
            }
          }
        },
        yAxis: {
          title: {
            text: ''
          },
          min: 0,
          tickAmount: 6,
          labels: {
            x: -25,
            ...(yFormatter
              ? {
                  formatter() {
                    return yFormatter(this.value);
                  }
                }
              : {})
          }
        },
        tooltip: {
          hideDelay: 0,
          useHTML: true,
          backgroundColor: 'none',
          borderWidth: 0,
          shadow: false,
          padding: 0,
          outside: true,
          formatter() {
            const points = isMultiSeries
              ? getMultiSeriesSharedPoints(this)
              : [];

            const { options } = this.point;

            const displayEndDate = getEndDateForTooltip(this.x.before);
            return toolTipFormatter({
              title: getToolTipTitleByDateRange(
                this.x.after,
                displayEndDate,
                granularity,
                this.x.name
              ),
              data: yFormatter ? yFormatter(this.y, 2) : this.y,
              label: labelBuilder(
                displayLabel,
                this.percentage,
                percentOffName,
                false,
                metricName,
                metricsCalculationConfig
              ),
              rawData: _.get(options, 'rawData'),
              currentPercentage: _.get(options, 'percentage'),
              computedMetrics: options?.computedMetrics,
              metricName,
              isClickable,
              isAreaSelectEnabled,
              shouldHideClickableMessage,
              points: _.map(points, point => ({
                ...point,
                data: yFormatter
                  ? yFormatter(_.get(point, 'y'), 2)
                  : _.get(point, 'y')
              }))
            });
          }
        },
        series: seriesHandleClick,
        plotOptions: {
          series: {
            point: {
              events: {
                mouseOver: onPointHover,
                mouseOut() {
                  onPointHover(null);
                }
              }
            },
            stickyTracking: false,
            events: {
              legendItemClick: e =>
                onLegendSeriesClick(
                  e,
                  metricName,
                  toggleDisabledSeriesIdsByMetricName
                )
            }
          }
        }
      }}
      chartType={chartType}
      chartColor={graphColor || 'dodgerBlue'}
    />
  );
};

export default CustomReportAreaChart;
